import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

const API_BASE = '/api/sites'

interface ReportSchedule {
  id: string
  site_id: string
  frequency: string
  recipient_email: string
  recipient_name: string | null
  include_sections: string // JSON array
  next_send_at: string | null
  last_sent_at: string | null
  enabled: number
  created_at: string
  updated_at: string
}

interface ReportHistoryItem {
  id: string
  report_schedule_id: string
  site_id: string
  sent_at: string
  recipient_email: string
  subject: string
  status: string
  error_message: string | null
}

export function useReportSchedules(siteId: string) {
  return useQuery({
    queryKey: ['reports', siteId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/${siteId}/reports`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch report schedules')
      return res.json() as Promise<{ schedules: ReportSchedule[] }>
    },
    enabled: !!siteId,
  })
}

export function useReportHistory(siteId: string) {
  return useQuery({
    queryKey: ['reports', siteId, 'history'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/${siteId}/reports/history`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch report history')
      return res.json() as Promise<{ history: ReportHistoryItem[] }>
    },
    enabled: !!siteId,
  })
}

export function useCreateReport(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      recipientEmail: string
      recipientName?: string
      frequency?: string
      includeSections?: string[]
    }) => {
      const res = await fetch(`${API_BASE}/${siteId}/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to create report')
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports', siteId] }),
  })
}

export function useUpdateReport(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reportId, ...data }: {
      reportId: string
      frequency?: string
      recipientEmail?: string
      recipientName?: string
      enabled?: boolean
      includeSections?: string[]
    }) => {
      const res = await fetch(`${API_BASE}/${siteId}/reports/${reportId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to update report')
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports', siteId] }),
  })
}

export function useDeleteReport(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (reportId: string) => {
      const res = await fetch(`${API_BASE}/${siteId}/reports/${reportId}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete report')
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports', siteId] }),
  })
}

export function useSendReport(siteId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (reportId: string) => {
      const res = await fetch(`${API_BASE}/${siteId}/reports/${reportId}/send-now`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to send report')
      return res.json() as Promise<{ ok: boolean; error?: string }>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports', siteId] })
      qc.invalidateQueries({ queryKey: ['reports', siteId, 'history'] })
    },
  })
}
